import React, { useState, useEffect } from 'react';
import { injectIntl } from 'react-intl';
import { i18nIntl, encodeBase64Fake, getRemotedbUrl, colorToArray, isNotEmpty, removeHTMLTags, isColorBright, RGB_2_HEX, stringifyArrayOfItems } from '../../lib/ep.js';
import { useCSS } from '../../lib/hooks.js';

import ActionButton, { SquaredButton } from '../builder/ActionButton.js';
import TimeCounterSimple from '../builder/TimeCounterSimple.js';
import QRCode from 'qrcode.react';
import Pin from '../builder/Pin';
import { getLayout } from '../../lib/ep.js';
import FloatingLayerPublic from '../front/FloatingLayerPublic.js';
import SwipeSlider from '../builder/SwipeSlider.js';
import ActionLink from '../builder/ActionLink.js';
import LeveledHTMLText from '../front/helpers/LeveledHTMLText.js';
import Spinner from '../builder/Spinner.js';
import TermsButton from '../front/buttons/TermsButton.js';

export const pin_length = 4;

const TimeCounterAnimated = ({ seconds, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const max_seconds_to_animate = 3600;	// 1 h

	const size = props?.size ?? 130;		// px
	const stroke_w = props?.stroke_w ?? 4;	// px

	const total_seconds_anim = Math.min(max_seconds_to_animate, props?.total_seconds ?? seconds);
	const ellapsed_time = (total_seconds_anim - seconds) / total_seconds_anim;

	let time_format = `{m}:{s}`;
	if(seconds >= 60 * 60) time_format = `{h}:{m}:{s}`;
	if(seconds > 24 * 60 * 60) time_format = `<span class="days">{d2}&nbsp;${i18n("day")}</span> {h}:{m}:{s}`;
	if(seconds > 48 * 60 * 60) time_format = `<span class="days">{d2}&nbsp;${i18n("days")}</span> {h}:{m}:{s}`;

	const is_builder = props?.is_builder ?? false;

	const stroke_length = Math.ceil(100 * size * Math.PI) / 100;

	useEffect(() => {
		let dynamicStyles = null;

		function addAnimation(body) {
			if(!dynamicStyles) {
				dynamicStyles = document.createElement('style');
				document.head.appendChild(dynamicStyles);
			}
			dynamicStyles.sheet.insertRule(body, dynamicStyles.length);
		}

		addAnimation(`
			@keyframes contdown_stroke {
				0% {
					stroke-dashoffset:-${Math.ceil(100 * ellapsed_time * stroke_length) / 100};
				}
				100% {
					stroke-dashoffset:-${stroke_length};
				}
			}
		`);

		// we only display the animation if we have, at most, max_seconds_to_animate seconds left in the counter:
		const pending_to_animate = parseInt(Math.max(0, seconds - max_seconds_to_animate));

		if(pending_to_animate < 24 * 60 * 60) {
			// setTimeout has a max limit of 2147483.647 ms (around 25 days),
			// so we limit this to 1 day:
			setTimeout(() => {
				document.querySelector('.time_animated').style.animation = `contdown_stroke ${Math.min(seconds, 3600)}s linear 0s${(is_builder) ? ' infinite' : ''} forwards`;
			}, (pending_to_animate * 1000));

			/*if(!is_builder && props.onFinish != undefined) {
				setTimeout(() => {
					props.onFinish();
				}, (seconds * 1000));
			}*/
		}
	}, [ seconds ]);

	const real_size = size + 4 * stroke_w;

	return (
		<div class="gui_animated_time_counter">
			<p>{i18n(props.children)}</p>

			<div class="time_container" style={{ width:`${real_size}px`, height:`${real_size}px` }}>
				<svg version="1.1" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" viewBox={`0 0 ${real_size} ${real_size}`}>
					<ellipse
						class="time_animated_bg"
						cx={size / 2 + 2 * stroke_w}
						cy={size / 2 + 2 * stroke_w}
						rx={(size + stroke_w) / 2}
						ry={(size + stroke_w) / 2}
					/>

					<ellipse
						class="time_animated"
						cx={size / 2 + 2 * stroke_w}
						cy={size / 2 + 2 * stroke_w}
						rx={size / 2}
						ry={size / 2}
						strokeWidth={stroke_w}
						strokeDasharray={`${stroke_length} ${stroke_length}`}
					/>
				</svg>

				<TimeCounterSimple
					seconds={seconds}
					execute_countdown={true}
					custom_render={time_format}
					_class={{ other:'time_text' }}
					onFinish={() => props.onFinish()}
				/>
			</div>
		</div>
	)
};

const getCodeValue = (_prize, is_builder = false) => {
	const prizeType = _prize.PrizeType ?? false;
	const prize = _prize.Prize ?? false;
	const promoCode = _prize.PromoCode ?? false;

	if((prizeType.type == '1' && promoCode.id > 0)) {
		return (!is_builder) ? promoCode.code : '123456789';
	}

	if(prizeType.redeemable || prizeType.type == '0') {
		return (!is_builder) ? `${prize.id}${prize.qr_code}` : '12345679123';
	}

	return false;
};

const QR = (props) => {
	const [ display_floating_qr, setDisplayFloatingQR ] = useState(false);

	const promotion = props?.promotion ?? false;
	const url = props?.url ?? false;
	const is_builder = props?.is_builder ?? false;
	const as_link = props?.as_link ?? false;

	const qr_size = Math.min(window.innerWidth - 60, window.innerHeight - 60, 300);

	const close = () => {
		setDisplayFloatingQR(false);
	};

	return (
		<>
			{(as_link) ? (
				<ActionLink
					onClick={(!is_builder) ? () => setDisplayFloatingQR(true) : null}
				>{props?.children ?? "view QR"}</ActionLink>
			) : (
				<SquaredButton
					icon='knk-qr'
					onClick={(!is_builder) ? () => setDisplayFloatingQR(true) : null}
					aria-label="View QR"
				/>
			)}

			{(display_floating_qr) && (
				<FloatingLayerPublic pm={promotion} backButton={close}>
					<div onClick={close} class="qr_code small" style={{ textAlign:'center'}}>
						<QRCode value={url} size={qr_size} />
					</div>
				</FloatingLayerPublic>
			)}
		</>
	)
}

const RedeemHeader = ({ promotion, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ display_sites_or_instructions, setDisplaySitesOrInstructions ] = useState(false);

	const code = props?.code ?? false;
	const is_builder = props?.is_builder ?? false;
	const enabled = props?.enabled ?? true;
	const instructions = props?.instructions ?? false;
	const has_validation_portal = (promotion.code_validator == 0) ? false : true;
	
	const prize_pos_names = isNotEmpty(props,'validation_site_names',['']) ? props?.validation_site_names : [];
	if(prize_pos_names.length > 0) {
		prize_pos_names.sort((a, b) => {
			if(a > b) return 1;
			if(a < b) return -1;
			return 0;
		});
	}

	const pos_names_str = stringifyArrayOfItems(prize_pos_names, i18n(" and "));

	return (
		<header class="redeem_header">
			{(prize_pos_names.length > 0 || instructions) && (
				<SquaredButton
					icon='knk-information'
					_class={{ other:'redeem_extra_info' }}
					onClick={() => setDisplaySitesOrInstructions(true)}
				/>
			)}

			{(promotion.full_reward_logo) && (
				<div class="logo">
					<img src={promotion.full_reward_logo} height="100%" />
				</div>
			)}

			<div class="prize_resumee">
				<div class="prize">
					<p>{i18n("Prize")}:</p>

					<h4>
						<span dangerouslySetInnerHTML={{ __html:props.title }} />
					</h4>
				</div>

				{(code) && (
					<div class="reference">
						<p>{i18n("Reference")}:</p>

						<h4>
							<span>
								{(enabled && has_validation_portal) ? (
									<QR
										is_builder={is_builder}
										promotion={promotion}
										url={props.qr_url}
										as_link={true}
									>{code}</QR>
								) : (
									code
								)}
							</span>
						</h4>
					</div>
				)}

				<div class="campaign">
					<p>{i18n("Campaign")}:</p>

					<h4>
						<span dangerouslySetInnerHTML={{ __html:(isNotEmpty(promotion, 'reward_name')) ? promotion.reward_name : (isNotEmpty(promotion, 'title') ? removeHTMLTags(promotion.title) : `#${promotion.id}` ) }} />
					</h4>
				</div>
			</div>

			{(display_sites_or_instructions) && (
				<FloatingLayerPublic
					pm={promotion}
					backButton={() => setDisplaySitesOrInstructions(false)}
				>
					<div class="prize_winner_content">
						{(instructions) && (
							<div class="prize_instructions in_top">
								<h4 class="group_title">{i18n("Instructions for use")}</h4>

								<LeveledHTMLText
									_class={{ other:'gui_text_align_start' }}
								>{instructions}</LeveledHTMLText>
							</div>
						)}

						{(prize_pos_names.length > 0) && (
							<div class={`prize_valid_in${(!instructions) ? ' in_top' : ''}`}>
								<h4 class="group_title">{i18n("Valid in")}</h4>

								<LeveledHTMLText
									_class={{ other:'gui_text_align_start' }}
								>{pos_names_str}</LeveledHTMLText>
							</div>
						)}
					</div>
				</FloatingLayerPublic>
			)}
		</header>
	)
};

export const RedeemedStatus = (props) => {
	useCSS('comp/redeem_prize.css');

	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const styles = {
		'--main-bg-color':props?.bg_color ?? 'var(--ep-green-color)',
		'--main-text-color':props?.text_color ?? 'var(--ep-white-color), 1',
	};

	const msg_placeholder = `<h1>${i18n("Prize redeemed")}</h1>`;
	const message = (isNotEmpty(props, 'message')) ? props.message : msg_placeholder;

	return (
		<div class="gui_not_in_bottom" style={styles}>
			<article class="redeem_contents" style={{ '--text-block-width':'100%' }}>
				<span class={`main_icon ${props?.icon ?? 'ep-check-list-item'}`} />

				<LeveledHTMLText>
					{message}
				</LeveledHTMLText>
			</article>
		</div>
	)
};

const Wrapper = ({ children, main_color, extra_classes, ...props }) => {
	const classes = ['gui_redeem_prize', 'awesome'];
	if(extra_classes != undefined) classes.push(extra_classes);
	

	return (
		<div class={classes.join(" ")} style={{ '--main-color':`${main_color.join(", ")}` }}>
			<section class="contents">
				<RedeemHeader {...props} />

				<article class="redeem_contents" style={{ '--text-block-width':'100%' }}>
					{children}
				</article>
			</section>
		</div>
	)
};

const RedeemPrize = (props) => {
	useCSS('comp/redeem_prize.css');

	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const promotion = props?.promotion ?? false;

	const [ code_pin, setCodePin ] = useState("");
	const [ result, setResult ] = useState(props?.result ?? {});
	const [ pin_error, setPinError ] = useState(props?.pin_error ?? false);
	const [ validating, setValidating ] = useState(false);
	const [ validating_slide, setValidatingSlide ] = useState(false);
	const [ pin_enabled, setPinEnabled ] = useState(promotion?.reward_pin_enabled ?? false);
	const [ display_slide, setDisplaySlide ] = useState((pin_enabled) ? false : true);

	useEffect(() => {
		const _pin_enabled = props?.promotion?.reward_pin_enabled ?? false;
		setPinEnabled(_pin_enabled);
		setDisplaySlide(!_pin_enabled);
	}, [ props.promotion.reward_pin_enabled ]);

	useEffect(() => {
		if(code_pin.length == pin_length)
			validatePin();

		if(code_pin == '')
			setPinError(false);

	}, [ code_pin ]);

	if(promotion === false) return null;

	const has_validation_portal = (promotion.code_validator == 0) ? false : true;
	
	const is_builder = props?.is_builder ?? false;

	const _prize = props?.prize ?? {};
	const prize = _prize?.Prize ?? {};
	const prizeType = _prize?.PrizeType ?? false;
	const is_expirable = (prizeType.prize_expiration_time || prizeType.prize_expiration_date) ? true : false;

	const cv_url = getRemotedbUrl(`${window.public_url}cv/${promotion.id}/${promotion.code_validator_hash}`);

	const changePin = (v) => {
		setPinError(false);
		setCodePin(v);
	};

	const validatePin = () => {
		setValidating(true);
		if(is_builder){
			setValidating(false);
			setDisplaySlide(true);
			return true;
		}

		$.post({
			url:getRemotedbUrl(`/redeem/${prize.id}/${prize.hash}`),
			data:{
				redeem:encodeBase64Fake({ pin:code_pin, check_prize:false }),
			},
			dataType:'JSON',
			success:(response) => {
				if(!response.status) {
					setPinError({ pin:response?.reason ?? "Unkown error" });
					setTimeout(() => {
						setCodePin("");
					}, 700);
				} else {
					setDisplaySlide(true);
				}
				setValidating(false);
			},
			error:(jqXHR, textStatus, errorThrown) => {
				setTimeout(() => {
					setCodePin("");
				}, 700);
				setValidating(false);
				setPinError({ pin:errorThrown });
			}
		});
	};

	const redeem = (cb) => {
		if(is_builder) {
			setResult({ status:1, seconds_ago:'-65', reason:'redeemed' });
			return true;
		}

		setValidatingSlide(true);
		$.post({
			url:getRemotedbUrl(`/redeem/${prize.id}/${prize.hash}`),
			data:{
				redeem:encodeBase64Fake({ pin:code_pin, check_prize:true }),
			},
			dataType:'json',
			success:(response) => {
				setValidatingSlide(false);
				setResult(response);
			},
			error:(jqXHR, textStatus, errorThrown) => {
				setValidatingSlide(false);
				setResult({ status:0, reason:errorThrown });
			}
		});
	};

	//const display_slide = (promotion.reward_slider && promotion.reward_pin_enabled && code_pin.length != pin_length) ? true : false;

	const _code = getCodeValue(_prize, is_builder);
	const _url = (has_validation_portal) ? `${cv_url}?code=${_code}` : false;
	const display_inline_qr = (prizeType && prizeType.redeemable && has_validation_portal && !promotion.reward_slider) ? true : false;

	const is_expired = (result.reason == 'expired') ? true : false;
	const is_redeemed = (result.reason == 'redeemed') ? true : false;

	if(result.reason != undefined && !is_expired && !is_redeemed) {
		return (
			<>
				<span>{result.reason}</span>

				<ActionButton
					onClick={() => setResult({})}
				>Reload</ActionButton>
			</>
		)
	}

	const layout_props = getLayout(promotion);
	const main_color = colorToArray(promotion?.reward_color ?? layout_props?.color?.main_color);

	const wrapper_props = {
		main_color,
		promotion,
		code:_code,
		title:prizeType.title,
		is_builder,
		qr_url:_url,
		intl:props.intl,
		instructions:isNotEmpty(prizeType, 'instructions', ['']) ? prizeType.instructions : false,
		validation_site_names:isNotEmpty(result, 'validation_site_names') ? result.validation_site_names : false,
	};

	if(is_expired) {
		return (
			<Wrapper
				{...wrapper_props}
				extra_classes='is_expired'
				enabled={false}
			>
				<span class="main_icon ep-delete-circle" />

				<LeveledHTMLText>
					<h1>{i18n("Prize has expired")}</h1>
				</LeveledHTMLText>
			</Wrapper>
		)
	}

	if(is_redeemed) {
		return (
			<Wrapper
				{...wrapper_props}
				extra_classes='is_redeemed'
				enabled={false}
			>
				<span class="main_icon ep-check-list-item" />

				<LeveledHTMLText>
					<h1>{i18n("Prize redeemed")}</h1>

					<p>{EPDateFromNow(EPDateSubtract(null, 'seconds', result.seconds_ago))}</p>
				</LeveledHTMLText>
			</Wrapper>
		)
	}

	let placeholder_instructions = "Present this QR code to the store staff to collect your prize.";
	if(promotion.reward_slider) {
		if(pin_enabled) {
			placeholder_instructions = "Present this screen to the store staff, and they will provide you with the security PIN required to collect your prize.";
		} else {
			placeholder_instructions = "Show the button to the store staff to slide it. Make sure to slide it only when you're in the store and your prize is ready.";
		}
	}

	const redeem_instructions = (isNotEmpty(promotion, 'reward_instructions')) ? promotion.reward_instructions : i18n(placeholder_instructions);
	const display_toc = isNotEmpty(promotion, 'reward_toc') ? true : false;

	const is_color_bright = (isColorBright(RGB_2_HEX(`rgba(${main_color.join(", ")})`))) ? true : false;

	return (
		<Wrapper
			{...wrapper_props}
			extra_classes='is_active'
			enabled={true}
		>
			{(is_expirable) && (
				<TimeCounterAnimated
					seconds={(!is_builder) ? result.seconds_to_redeem : (15 * 60)}
					total_seconds={(!is_builder) ? result.seconds_from_win : (15 * 60)}
					is_builder={is_builder}
					onFinish={() => {
						setResult({ reason:'expired' });
					}}
					intl={props.intl}
				>Redeem your prize in</TimeCounterAnimated>
			)}

			<p dangerouslySetInnerHTML={{ __html:redeem_instructions.replace(/\n/g, '<br />') }} />

			{(display_inline_qr) && (
				<QRCode
					value={_url}
					size={150}
				/>
			)}

			{(promotion.reward_slider) && (
				<div class={`redeem_actions${(pin_error.pin != undefined) ? ' shake_it' : ''}`}>
					{(pin_enabled && !display_slide) && (
						<Pin
							name='pin'
							value={code_pin}
							pin_length={pin_length}
							onChange={changePin}
							errors={pin_error}
							hide_error_msg={true}
							_class={{ first:true }}
							disabled={validating}
						>Enter the PIN code</Pin>
					)}

					{(display_slide && !validating) && (
						<SwipeSlider
							color={`rgba(${main_color.join(", ")})`}
							text_unlocked={i18n("Prize redeemed")}
							onSuccess={redeem}
							onReset={() => {
							}}
						>{(isNotEmpty(promotion, 'reward_slider_label')) ? promotion.reward_slider_label : i18n("Slide after pickup")}</SwipeSlider>
					)}

					{(display_slide && validating_slide) && (
						<Spinner
							color={(is_color_bright) ? '#000000' : '#FFFFFF'}
						/>
					)}
				</div>
			)}
			
			{(display_toc) && (
				<TermsButton
					promotion={promotion}
					icon={false}
				/>
			)}
		</Wrapper>
	)
};

export default injectIntl(RedeemPrize);